import { getErrorMessage } from "./errorMessage";

export const CASH_REFUND_REASONS = [
  { value: "CUSTOMER_REQUEST", label: "Customer request" },
  { value: "INCORRECT_ORDER", label: "Incorrect order" },
  { value: "ITEM_UNAVAILABLE", label: "Item unavailable" },
  { value: "DUPLICATE_PAYMENT", label: "Duplicate payment" },
  { value: "SERVICE_ISSUE", label: "Service issue" },
  { value: "OTHER", label: "Other" },
];

const ERROR_MESSAGES = {
  ORDER_NOT_FOUND: "Order not found. Check the order number and try again.",
  DEVICE_NOT_FOUND: "POS device not found. Please sign in or select the device again.",
  FORBIDDEN: "This refund is not allowed for the current user or device.",
  REFUND_ALREADY_PROCESSED: "A refund has already been processed for this order.",
  REFUND_PAYMENT_NOT_CAPTURED: "No captured payment is available for this order.",
  REFUND_PAYMENT_METHOD_INVALID: "Cash refunds are available only for fully cash-paid orders.",
  REFUND_AMOUNT_INVALID: "The refund amount is invalid.",
  REFUND_AMOUNT_EXCEEDS_CAPTURED: "The refund amount exceeds the captured cash payment.",
};

const roundCurrency = (value) =>
  Math.round((Number(value) + Number.EPSILON) * 100) / 100;

export const createCashRefundIdempotencyKey = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return `cash-refund-${crypto.randomUUID()}`;
  }
  return `cash-refund-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const formatCashRefundMoney = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Number(value || 0));

export const getCashRefundError = (error, fallback) => {
  const code = String(error?.data?.title || error?.data?.code || "")
    .trim()
    .toUpperCase();
  return ERROR_MESSAGES[code] || getErrorMessage(error, fallback);
};

export const getRecordedRefundReason = (payments) => {
  const refundedPayment = (Array.isArray(payments) ? payments : []).find(
    (payment) =>
      String(payment?.paymentStatus || "").toUpperCase() === "REFUNDED",
  );

  return String(
    refundedPayment?.changeReason || refundedPayment?.notes || "",
  ).trim();
};

export const getRefundPaymentState = (
  payments,
  grandTotal,
  paymentMethod = "CASH",
) => {
  const rows = Array.isArray(payments) ? payments : [];
  const normalizedMethod = String(paymentMethod || "CASH").toUpperCase();
  const allCaptured = rows.filter(
    (payment) =>
      String(payment?.paymentStatus || "").toUpperCase() === "CAPTURED" &&
      Number(payment?.amount || 0) > 0,
  );
  const hasRefund = rows.some(
    (payment) =>
      String(payment?.paymentStatus || "").toUpperCase() === "REFUNDED",
  );
  const capturedTotal = roundCurrency(
    allCaptured.reduce(
      (sum, payment) => sum + Number(payment?.amount || 0),
      0,
    ),
  );
  const orderTotal = roundCurrency(grandTotal);
  const refundableAmount = roundCurrency(
    Math.min(capturedTotal, orderTotal > 0 ? orderTotal : capturedTotal),
  );

  let ineligibleReason = "";
  if (hasRefund) {
    ineligibleReason = "A refund has already been recorded for this order.";
  } else if (allCaptured.length === 0) {
    ineligibleReason = "No captured payment is available for this order.";
  } else if (refundableAmount <= 0) {
    ineligibleReason = `This order has no refundable ${normalizedMethod.toLowerCase()} balance.`;
  }

  return {
    captured: allCaptured,
    capturedTotal,
    currencyCode: allCaptured[0]?.currencyCode || "USD",
    refundableAmount,
    hasRefund,
    isEligible: !ineligibleReason,
    ineligibleReason,
  };
};

export const getCashRefundPaymentState = (payments, grandTotal) =>
  getRefundPaymentState(payments, grandTotal, "CASH");
